/* EqFoonote Class
 * Takes a module and adds references/footnote links and back-links as needed
 * ==== Usage Notes ====
 * Use the following attributes to markup your HTML to indicate the following
 * Add the "eq-footnote" class around the module or div surrounding a group of notes and references
 * 
 * Attributes
 * data-footnote-title: Sets the title of the group of footnotes for accessibility
 * If this attribute isn't used, it will attempt to derive the title from the first h2 of the module
 * 
 * data-footnote-ref: number, ID number of the footnote the reference should link to
 * 
 * data-footnote: number, ID number associated with the footnote
 * Optional, if footnotes are contained within an element all next to each other, add class 'eq-footnote-list' to the parent element
 * 
 * Example HTML
 * <div class="module eq-footnote" data-footnote-title="Module Title">
 *  <p class="example-footnote" data-footnote-ref="1">Example data text</p>
 *  <p class="example-source" data-footnote="1">Example source text</p>
 * </div>
 */
class EqFootnote {
    constructor(selector = '.eq-footnote') {
        const footnote_modules = document.querySelectorAll(selector);
        this.modules = Array();
        footnote_modules.forEach((module, module_index) => {
            let title = '';
            if (module.dataset.footnoteTitle) {
                title = ' ' + module.dataset.footnoteTitle;
            }
            const row_title = module.querySelector('h2');
            if (!title && row_title) {
                title = ' ' + row_title.innerText;
            }
            if (!title) {
                title = ` Module ${module_index}`;
            }
            this.build_references(module, title);
            this.build_footnotes(module, title);
        });
    }

    build_references(module, title) {
        const references = module.querySelectorAll('[data-footnote-ref]');
        const module_id = this.slugify(title);
        references.forEach((ref) => {
            const ref_text = ref.innerText;
            const footnote_id = ref.dataset.footnoteRef;
            const footnote_link = document.createElement('a');
            footnote_link.innerText = ref_text;
            footnote_link.setAttribute('aria-label', `Reference to${title} footnote ${footnote_id}`);
            let ref_id = `${module_id}-footnote-${footnote_id}-ref`;
            let ref_count = 2;
            while (document.getElementById(ref_id)) {
                ref_id = `${module_id}-footnote-${footnote_id}-ref-${ref_count++}`;
            }
            footnote_link.setAttribute('href', `#${module_id}-footnote-${footnote_id}`);
            ref.replaceChildren(footnote_link);
            this.add_anchor(ref, ref_id);
        });
    }

    build_footnotes(module, title) {
        let footnotes = module.querySelectorAll('[data-footnote]');
        if (footnotes.length == 0) {
            footnotes = module.querySelectorAll('.eq-footnote-list > *');
            return;
        }
        const module_id = this.slugify(title);
        footnotes.forEach((footnote, index) => {
            const footnote_key = footnote.dataset.footnote ? footnote.dataset.footnote : index + 1;
            const footnote_id = `${module_id}-footnote-${footnote_key}`;
            if (document.getElementById(footnote_id)) {
                console.error(`Duplicate footnote:${title}, Footnote ${footnote_key}`);
                return;
            }
            this.add_anchor(footnote, footnote_id);
            // Only first instance of the reference will get a back link in the footer
            const ref_id = `${module_id}-footnote-${footnote_key}-ref`;
            const target_ref = document.getElementById(ref_id);
            if (!target_ref) {
                console.error(`Could not find reference ${ref_id} to backlink for${title} footnote ${footnote_id}`);
                return;
            }
            const link_back = document.createElement('a');
            link_back.innerText = '⮑';
            link_back.setAttribute('href', `#${ref_id}`);
            link_back.setAttribute('aria-label', `Return to${title} footnote ${footnote_key} reference`);
            link_back.classList.add('link-back');
            footnote.insertAdjacentElement('beforeend', link_back);
        });
    }

    slugify(str) {
        return String(str)
            .trim()
            .toLowerCase()
            .replace(/[^\w\s-]/g, '')
            .replace(/[\s_-]+/g, '-')
            .replace(/^-+|-+$/g, '');
    }

    // Adds a span with the class "anchor" as the first child of an element to aid in proper positioining when jumping to footnotes
    add_anchor(element, id) {
        const anchor = document.createElement('span');
        anchor.classList.add('anchor');
        anchor.setAttribute('id', id);
        element.insertAdjacentElement('afterbegin', anchor);
    }
}